import { useCallback } from 'react';
import { useAuth } from '../context';
import { MembershipSource } from '../constants/membership-source';
import { useSwitchAccount } from './use-switch-account';
import {
  forgetMasterAccountBoardUrl,
  peekMasterAccountBoardUrl
} from '../utils/master-account-board-return';

export interface UseReturnToMasterAccountBoard {
  /** The master account managing the client being acted in through a master account membership, else null. */
  masterAccount: { id: string; name: string } | null;
  /** Switches back to the master account, onto the board URL saved on the way out. */
  returnToMasterAccountBoard: () => void;
  returning: boolean;
}

/** Leaves a client reached through a master account, switching back into the master account's board. */
export function useReturnToMasterAccountBoard(): UseReturnToMasterAccountBoard {
  const { user } = useAuth();
  const { switchToMasterAccount, switching } = useSwitchAccount();

  const masterAccount =
    user?.source === MembershipSource.MASTER_ACCOUNT
      ? (user.account?.managingMasterAccount ?? null)
      : null;

  const returnToMasterAccountBoard = useCallback(() => {
    if (!masterAccount) return;
    // No saved URL lands on the board home; a refused switch keeps it for the next try.
    void switchToMasterAccount(
      masterAccount.id,
      peekMasterAccountBoardUrl(masterAccount.id) ?? undefined
    ).then(left => {
      if (left) forgetMasterAccountBoardUrl();
    });
  }, [masterAccount, switchToMasterAccount]);

  return { masterAccount, returnToMasterAccountBoard, returning: switching };
}
